import { memo, useEffect, useRef, useState } from 'react';

import { RouteChangeListener } from '../RouteChangeListener';

import { MicroFrontendLoader } from './MicroFrontendLoader';
import { MicroFrontendMemo } from './MicroFrontendMemo';
import type {
  IsolatedMicroFrontendAPI,
  IsolatedMicroFrontendType,
  MicroFrontendType,
} from './MicroFrontendTypes';

const SelfRenderedMicroFrontendForIframe = memo(
  ({
    moduleName,
    isolatedAPI,
    ...props
  }: {
    moduleName: string;
    isolatedAPI: IsolatedMicroFrontendAPI;
    [key: string]: any;
  }) => {
    const rootNodeRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      isolatedAPI.render(rootNodeRef.current as HTMLDivElement, props);
    });
    return (
      <>
        <div ref={rootNodeRef} id={`root_${moduleName}`} />
        <RouteChangeListener />
      </>
    );
  },
);

export function MicroFrontendIframeBootstraper({
  moduleName,
  withSpinner,
  ...props
}: {
  moduleName: string;
  withSpinner?: boolean;
  [key: string]: any;
}) {
  const [[Component, isolatedAPI, dependencies], setMFE] = useState<
    [
      ReturnType<MicroFrontendType['component']> | null,
      IsolatedMicroFrontendAPI | null,
      string[],
    ]
  >([null, null, []]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let isMounted = true;
    const callback = () => {
      if (!iframeRef.current?.contentWindow || !isMounted) {
        return;
      }
      const mfe = iframeRef.current.contentWindow as
        | (Window & MicroFrontendType)
        | (Window & IsolatedMicroFrontendType);
      const deps: string[] =
        typeof mfe.dependencies === 'function' ? mfe.dependencies() || [] : [];
      if ('render' in mfe) {
        setMFE([null, { render: mfe.render, unmount: mfe.unmount }, deps]);
      } else if ('component' in mfe) {
        setMFE([mfe.component(), null, deps]);
      } else {
        console.error(
          `No component nor render function found in ${moduleName}`,
        );
      }
      setLoading(false);
    };
    const eventName = `mfe_internal_${moduleName}_ready`;
    window.addEventListener(eventName, callback);
    return () => {
      isMounted = false;
      window.removeEventListener(eventName, callback);
    };
  }, [moduleName]);

  const componentProps: { [key: string]: any } = dependencies
    ? Object.fromEntries(dependencies.map((key) => [key, props[key]]))
    : {};

  return (
    <>
      <iframe
        id={`iframe_${moduleName}`}
        ref={iframeRef}
        src={`/micro-frontend.html?name=${encodeURIComponent(moduleName)}`}
        title={`${moduleName} bootstraper`}
        style={{ display: 'none' }}
      />
      {isLoading && withSpinner ? <MicroFrontendLoader /> : null}
      {Component ? (
        <MicroFrontendMemo Component={Component} {...componentProps} />
      ) : null}
      {isolatedAPI ? (
        <SelfRenderedMicroFrontendForIframe
          moduleName={moduleName}
          isolatedAPI={isolatedAPI}
          {...componentProps}
        />
      ) : null}
    </>
  );
}
